import React from "react";
import ReactPlayer from "react-player";
import { useLazyGetPodcastVideoQuery } from "../../../store/api/podcasts";
import { useAuthToken } from "../../../hooks/auth";
import { Box, CircularProgress, useMediaQuery, useTheme } from "@mui/material";
import { useMediaRef } from "media-chrome/react/media-store";

export function useCombinedRefs(...refs) {
  const targetRef = React.useRef();

  React.useEffect(() => {
    refs.forEach((ref) => {
      if (!ref) return;

      if (typeof ref === "function") {
        ref(targetRef.current);
      } else {
        ref.current = targetRef.current;
      }
    });
  }, [refs]);

  return targetRef;
}

const Player = React.forwardRef(
  (
    { media, watchType, isMaximized, poster, setIsMuted, setVolume, ...props },
    ref,
  ) => {
    const isMobile = useMediaQuery("(pointer: coarse)");
    const theme = useTheme();

    const mediaRef = useMediaRef();
    const playerRef = useCombinedRefs(mediaRef, ref);

    const video = React.useMemo(() => {
      return media;
    }, [media]);

    const isTrailer = React.useMemo(() => {
      if (!video) return false;
      return video.type === "trailer";
    }, [video]);

    const authToken = useAuthToken();
    const [queryGetPodcastVideo, { isLoading }] = useLazyGetPodcastVideoQuery();

    React.useEffect(() => {
      if (isTrailer) setFile(null);
    }, [isTrailer]);

    const [file, setFile] = React.useState(null);
    React.useEffect(() => {
      if (video && !isTrailer && file?.id !== video.id) {
        queryGetPodcastVideo({
          id: video.id,
          authToken,
        }).then((result) => {
          if (!result.isError) setFile(result.data);
        });
      }
    }, [video, authToken, isTrailer, file]);

    const fileURL = React.useMemo(() => {
      if (isTrailer) {
        return video.videoLink;
      } else if (file) {
        return file.medias?.play?.hls.link;
      }
      return null;
    }, [isTrailer, video, file]);

    const style = React.useMemo(() => {
      return {
        width: "100%",
        aspectRatio: watchType === "watch" ? "16/9" : "24/9",
      };
    }, [watchType]);

    if (isLoading || (!file && !isTrailer)) {
      return (
        <Box
          sx={{
            width: "100%",
            height: watchType === "listen" ? "150px" : undefined,
            background: "black",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress
            size={75}
            sx={{
              marginBottom: "20px",
            }}
          />
        </Box>
      );
    }

    return (
      <>
        <ReactPlayer
          ref={playerRef}
          slot="media"
          src={fileURL}
          controls={false}
          width="100%"
          height="100%" // Always fill the controller
          style={style}
          playsInline={true}
          {...props}
        />
        {watchType === "listen" && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "white",
              opacity: 1,
            }}
          >
            <Box
              sx={{
                backgroundImage: `url(${poster})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center",
                width: "100%",
                height: "100%",
                backdropFilter: "blur(5px)",
                filter: "blur(5px)",
              }}
            />
          </Box>
        )}
      </>
    );
  },
);

export default Player;
